import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { classNames } from 'support/helpers/generic/generic';
import { ErrorIcon } from 'components/DataTable/components/Icons';

import { CellProps } from '../../../types';
import { useCellClassNames } from '../../cellHelpers';
import { ViewWrapperCell } from '../ViewWrapperCell';

export type Props<TData, TValue> = CellContext<TData, TValue> & Pick<CellProps, 'fieldId' | 'isHighlighted'>;
export const ViewBooleanCell = <TData = unknown, TValue = string>({
  row,
  column,
  table,
  isHighlighted,
  fieldId,
}: Props<TData, TValue>) => {
  const { t } = useTranslation();
  const { service } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const value = useWatch({ name: fieldId });

  const {
    columnDef: { meta: columnMeta },
  } = column;

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });

  return (
    <ViewWrapperCell fieldId={fieldId} isHighlighted={isHighlighted} className={cellClassNames} meta={columnMeta}>
      <span className="flex w-full items-center">
        <div
          className={classNames('flex w-full gap-x-0.5 overflow-hidden', {
            'justify-start': columnMeta?.optionalConfig?.textAlign === 'left',
          })}
          style={{ width: columnMeta?.optionalConfig?.width }}
        >
          {typeof value === 'boolean' ? (
            <span className="flex-initial">{value ? t('common:yes') : t('common:no')}</span>
          ) : (
            <span className={classNames({ 'text-gray-400': !error })}>{EMPTY_FIELD}</span>
          )}
        </div>
      </span>
      {error ? <ErrorIcon /> : null}
    </ViewWrapperCell>
  );
};
